import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarClock,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  PanelRightOpen,
  PenLine,
  Send,
  Sparkles,
} from 'lucide-react'
import { isManualPlatform, type ScheduleItem, type ScheduleQuery } from '@/api/client'
import { usePostActions } from '@/lib/postActions'
import { statusOf } from '@/lib/status'
import { useCampaigns, useRedeemApps, useRedeemPosts, useSchedule } from '@/lib/queries'
import { dayKey, formatShort, parseTime } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  Button,
  CodeBlock,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  PlatformMark,
  Segmented,
  SkeletonRows,
  SourceTag,
  StatusDot,
  StatusPill,
  TimelineList,
  errorText,
  platformName,
  useIsDesktop,
  useQueryParam,
  useSetQueryParams,
  useToast,
  useWidePage,
  type Source,
  type TimelineMark,
} from '@/ui'
import { appLabel } from '@/lib/redeem'
import { DeskPostForm } from './DeskPostForm'
import { DraftForm } from './DraftForm'
import {
  abilities,
  channelLabel,
  headline,
  isWaiting,
  itemTime,
  ownerName,
  publishingFor,
  refOf,
  SlotForm,
  sourceOf,
  STUCK_MINUTES,
  useOpenDetail,
  usePostCommands,
  whereLabel,
  WritePostDialog,
} from './shared'

/** Statuses a whole history of is worth seeing at once — a failure from last
 *  week still needs a decision — so choosing one drops the week range. */
const ALL_TIME = new Set(['failed', 'publishing', 'unscheduled'])

const STATUS_FILTERS: { value: string; label: string; count?: keyof CountsT }[] = [
  { value: '', label: '상태 전체' },
  { value: 'scheduled', label: '예약', count: 'scheduled' },
  { value: 'publishing', label: '게시 중', count: 'publishing' },
  { value: 'failed', label: '실패', count: 'failed' },
  { value: 'unscheduled', label: '슬롯 없음', count: 'unscheduled' },
  { value: 'published', label: '게시됨', count: 'published' },
]
type CountsT = { scheduled: number; publishing: number; failed: number; unscheduled: number; published: number }

const PLATFORMS = ['reddit', 'bluesky', 'threads', 'ppomppu', 'okky', 'x', 'instagram', 'tiktok']

/** Monday 00:00 of the week holding d, in the browser's zone. */
export function weekStart(d: Date): Date {
  const back = (d.getDay() + 6) % 7
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - back)
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

function parseWeek(v: string, now: Date): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  return m ? weekStart(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : weekStart(now)
}

const md = (d: Date) => `${d.getMonth() + 1}. ${d.getDate()}.`

/** The post's dot on the rail, in its status's tone. */
const markOf = (it: ScheduleItem): TimelineMark => {
  const def = statusOf('post', it.status, { scheduledAt: it.scheduled_at })
  return { tone: def.tone, pulse: def.pulse }
}

/** The time, moved on each minute so the rail's 지금 line keeps up with a page
 *  left open. A fixed time (tests) stays put. */
function useMinuteClock(fixed?: Date): Date {
  const [t, setT] = useState(() => new Date())
  useEffect(() => {
    if (fixed) return
    const id = window.setInterval(() => setT(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [fixed])
  return fixed ?? t
}

/** The filters both schedule views share, read from the URL. */
export function useScheduleFilters() {
  const [source] = useQueryParam('source', '')
  const [platform, setPlatform] = useQueryParam('platform', '')
  const [appId] = useQueryParam('app_id', '')
  const [campaignId] = useQueryParam('campaign_id', '')
  const set = useSetQueryParams()
  // An app belongs to the desk and a campaign to the campaigns, so picking
  // one clears the other, and each source clears the owner it cannot have.
  // Each pick is one write; see useSetQueryParams.
  return {
    source: (source === 'redeem' || source === 'campaign' ? source : '') as '' | Source,
    setSource: (v: '' | Source) =>
      set({ source: v, ...(v === 'campaign' ? { app_id: '' } : v === 'redeem' ? { campaign_id: '' } : {}) }),
    platform,
    setPlatform,
    appId: Number(appId) || 0,
    setAppId: (v: string) => set(v ? { app_id: v, campaign_id: '' } : { app_id: '' }),
    campaignId: Number(campaignId) || 0,
    setCampaignId: (v: string) => set(v ? { campaign_id: v, app_id: '' } : { campaign_id: '' }),
  }
}

/** Source, platform and owner selects, plus the 목록|달력 switch. */
export function ScheduleFilterRow({
  view,
  filters,
  extra,
  end,
}: {
  view: 'list' | 'calendar'
  filters: ReturnType<typeof useScheduleFilters>
  extra?: ReactNode
  end?: ReactNode
}) {
  const navigate = useNavigate()
  const apps = useRedeemApps().data?.items ?? []
  const campaigns = useCampaigns().data?.items ?? []
  const { source, setSource, platform, setPlatform, appId, setAppId, campaignId, setCampaignId } = filters
  const switchView = (to: 'list' | 'calendar') => {
    const q = new URLSearchParams(window.location.search)
    q.delete('week')
    q.delete('month')
    q.delete('status')
    q.delete('lane')
    const s = q.toString()
    navigate(`${to === 'list' ? '/schedule' : '/schedule/calendar'}${s ? `?${s}` : ''}`)
  }
  return (
    <FilterBar end={end}>
      <Segmented
        label="보기"
        value={view}
        onChange={switchView}
        options={[
          { value: 'list', label: '목록' },
          { value: 'calendar', label: '달력' },
        ]}
      />
      <Segmented<'' | Source>
        label="출처"
        value={source}
        onChange={setSource}
        options={[
          { value: '', label: '전체' },
          { value: 'redeem', label: '리딤' },
          { value: 'campaign', label: '캠페인' },
        ]}
      />
      <FilterSelect
        label="플랫폼"
        value={platform}
        onChange={setPlatform}
        options={[{ value: '', label: '플랫폼 전체' }, ...PLATFORMS.map((p) => ({ value: p, label: platformName(p) }))]}
      />
      {source !== 'campaign' && (
        <FilterSelect
          label="앱"
          value={appId ? String(appId) : ''}
          onChange={setAppId}
          options={[
            { value: '', label: '앱 전체' },
            ...apps.map((a) => ({ value: String(a.app.id), label: appLabel(a.app, apps.map((x) => x.app)) })),
          ]}
        />
      )}
      {source !== 'redeem' && (
        <FilterSelect
          label="캠페인"
          value={campaignId ? String(campaignId) : ''}
          onChange={setCampaignId}
          options={[{ value: '', label: '캠페인 전체' }, ...campaigns.map((c) => ({ value: String(c.ID), label: c.Name }))]}
        />
      )}
      {extra}
    </FilterBar>
  )
}

/** 일정 — 목록. Both programmes' posts for a week, by day on a rail split into
 *  parts of the day, with the posts still waiting for a slot on top. */
export default function ScheduleList({ now: nowProp }: { now?: Date }) {
  useWidePage()
  const now = useMinuteClock(nowProp)
  const filters = useScheduleFilters()
  const [statusParam] = useQueryParam('status', '')
  const [laneParam] = useQueryParam('lane', '')
  const setQuery = useSetQueryParams()
  const [week, setWeek] = useQueryParam('week', '')
  const [writing, setWriting] = useState(false)
  const status = statusParam || (laneParam === 'unscheduled' ? 'unscheduled' : '')
  const start = parseWeek(week, now)
  const allTime = ALL_TIME.has(status)

  const q: ScheduleQuery = {
    from: allTime ? undefined : start.toISOString(),
    to: allTime ? undefined : addDays(start, 7).toISOString(),
    source: filters.source,
    status: status || undefined,
    appId: filters.appId || undefined,
    campaignId: filters.campaignId || undefined,
    platform: filters.platform || undefined,
    includeUnscheduled: !status,
  }
  const feed = useSchedule(q, { live: true })
  const items = feed.data?.items ?? []
  const counts = feed.data?.counts
  const waiting = items.filter(isWaiting)
  const timed = items.filter((i) => !waiting.includes(i))
  const thisWeek = dayKey(start) === dayKey(weekStart(now))
  // The current week starts at today: what went out on earlier days of it
  // waits under a toggle instead of filling the top. A failure or a stuck
  // publish from those days stays in view — it still needs a decision.
  const past = thisWeek && !allTime ? timed.filter((i) => isPastPublished(i, now)) : []
  const upcoming = past.length > 0 ? timed.filter((i) => !past.includes(i)) : timed
  const commands = usePostCommands()

  const showManual = !status && filters.source !== 'campaign' && !filters.campaignId
  const manualDrafts = useRedeemPosts(filters.appId, 'draft')
  const manual = showManual
    ? (manualDrafts.data?.items ?? []).filter(
        (p) => isManualPlatform(p.platform) && (!filters.platform || p.platform === filters.platform),
      )
    : []

  const weekLabel = `${md(start)} – ${md(addDays(start, 6))}`

  return (
    <>
      <PageHeader
        title="일정"
        actions={
          <Button variant="primary" icon={PenLine} onClick={() => setWriting(true)}>
            글 쓰기
          </Button>
        }
      >
        <ScheduleFilterRow
          view="list"
          filters={filters}
          extra={
            <FilterSelect
              label="상태"
              value={status}
              onChange={(v) => setQuery({ status: v, lane: '' })}
              options={STATUS_FILTERS.map((s) => ({
                value: s.value,
                label: s.count && counts ? `${s.label} ${counts[s.count]}` : s.label,
              }))}
            />
          }
          end={
            allTime ? (
              <span className="text-meta text-muted-foreground">기간 무관 · {items.length}건</span>
            ) : (
              <div className="flex items-center gap-1">
                <IconButton label="이전 주" icon={ChevronLeft} size="sm" onClick={() => setWeek(dayKey(addDays(start, -7)))} />
                <span className="min-w-[7.5rem] text-center text-table font-medium tabular-nums">{weekLabel}</span>
                <IconButton label="다음 주" icon={ChevronRight} size="sm" onClick={() => setWeek(dayKey(addDays(start, 7)))} />
                {!thisWeek && (
                  <Button size="sm" onClick={() => setWeek('')}>
                    이번 주
                  </Button>
                )}
              </div>
            )
          }
        />
      </PageHeader>

      {feed.error ? (
        <ErrorState title="일정을 불러오지 못했다" error={feed.error} onRetry={() => void feed.refetch()} />
      ) : feed.isLoading ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="space-y-6">
          {waiting.length > 0 && (
            <WaitingLane
              // Filtered to 슬롯 없음, the lane is the whole page: open it.
              key={status === 'unscheduled' ? 'only' : 'mixed'}
              items={waiting}
              initiallyShown={status === 'unscheduled'}
              commands={commands}
            />
          )}

          {manual.length > 0 && <ManualLane posts={manual} />}

          {past.length > 0 && <PastLane items={past} now={now} commands={commands} />}

          <TimelineList
            label="일정"
            items={upcoming}
            now={now}
            getTime={itemTime}
            getKey={(it) => `${it.kind}-${it.id}`}
            getMark={markOf}
            renderItem={(it) => <ItemRow it={it} commands={commands} now={now} showTime={false} />}
            empty={
              waiting.length === 0 && manual.length === 0 ? (
                <EmptyState
                  icon={CalendarClock}
                  title={
                    allTime
                      ? '해당하는 글이 없다'
                      : past.length > 0
                        ? '오늘부터 잡힌 글이 없다'
                        : `${weekLabel}에 잡힌 글이 없다`
                  }
                  description={allTime ? undefined : '다른 주로 넘기거나, 글을 써서 예약한다.'}
                  actions={
                    <Button variant="primary" icon={PenLine} onClick={() => setWriting(true)}>
                      글 쓰기
                    </Button>
                  }
                />
              ) : null
            }
          />
        </div>
      )}

      {commands.dialog}
      <WritePostDialog
        open={writing}
        onClose={() => setWriting(false)}
        initial={filters.source === 'campaign' ? 'campaign' : 'redeem'}
        desk={(done) => <DeskPostForm onDone={done} initialAppId={filters.appId || undefined} />}
        campaign={(done) => <DraftForm onDone={done} />}
      />
    </>
  )
}

export type Commands = ReturnType<typeof usePostCommands>

function RowMeta({ it }: { it: ScheduleItem }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-meta text-muted-foreground">
      <SourceTag source={sourceOf(it)} />
      <PlatformMark platform={it.platform} />
      <span className="truncate">{channelLabel(it)}</span>
      {ownerName(it) && <span className="truncate">· {ownerName(it)}</span>}
    </div>
  )
}

function Thumb({ it }: { it: ScheduleItem }) {
  if (!it.media) return null
  return (
    <img
      src={`/api/media/${it.media.id}/file`}
      alt=""
      loading="lazy"
      className="hidden h-6 w-10 shrink-0 rounded border border-border object-cover sm:block"
    />
  )
}

/** One post on the timeline: its time, where it goes, what it says, its
 *  state, and what can be done to it from here. On a rail, which shows the
 *  time itself, showTime is off. */
export function ItemRow({
  it,
  commands,
  now,
  showTime = true,
}: {
  it: ScheduleItem
  commands: Commands
  now: Date
  showTime?: boolean
}) {
  const open = useOpenDetail()
  const desktop = useIsDesktop()
  const [moving, setMoving] = useState(false)
  const can = abilities(it)
  const t = itemTime(it)
  const stuck = (publishingFor(it, now) ?? 0) > STUCK_MINUTES
  const past = it.status === 'published'
  const time = t ? new Date(t) : null
  // On the phone the buttons go under the text, at a thumb's size, so the
  // title and the failure reason get the whole width.
  const tap = desktop ? undefined : 'h-10 w-10'
  const actions = (
    <div className={cn('flex shrink-0 items-center', !desktop && '-ml-2.5 mt-0.5')}>
      {can.publishNow && (
        <IconButton
          label={commands.isPublishing(refOf(it)) ? '게시하는 중' : '지금 게시'}
          icon={Send}
          size="sm"
          className={tap}
          disabled={commands.isPublishing(refOf(it))}
          onClick={() => commands.askPublish(refOf(it), whereLabel(it))}
        />
      )}
      {can.schedule && (
        <IconButton label={can.scheduleLabel} icon={CalendarClock} size="sm" className={tap} onClick={() => setMoving((v) => !v)} />
      )}
      {can.unschedule && (
        <IconButton
          label="내리기"
          icon={CalendarX2}
          size="sm"
          className={tap}
          onClick={() => commands.askUnschedule(refOf(it), whereLabel(it))}
        />
      )}
      {it.external_url && (
        <a
          href={it.external_url}
          target="_blank"
          rel="noreferrer"
          aria-label="글 열기"
          title={it.external_url}
          className={cn('inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-muted', tap)}
        >
          <ExternalLink size={16} aria-hidden />
        </a>
      )}
      <IconButton label="열기" icon={PanelRightOpen} size="sm" className={tap} onClick={() => open(refOf(it))} />
    </div>
  )
  return (
    <div className={cn('px-3 py-2.5', it.status === 'failed' && 'bg-status-failed-soft/40')}>
      <div className="flex items-start gap-3">
        {showTime && (
          <time
            dateTime={t ?? undefined}
            title={t ? formatShort(t, now) : undefined}
            className={cn('w-11 shrink-0 pt-0.5 text-table font-semibold tabular-nums', past && 'text-muted-foreground')}
          >
            {time ? `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}` : '—'}
          </time>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <RowMeta it={it} />
            <StatusPill domain="post" status={it.status} scheduledAt={it.scheduled_at} />
            {it.announced_at && it.status !== 'published' && (
              <span className="inline-flex items-center gap-1 text-micro text-muted-foreground" title="텔레그램 알림을 보냈다">
                <StatusDot tone="scheduled" />
                알림
              </span>
            )}
            {stuck && <span className="text-micro font-medium text-status-publishing">{STUCK_MINUTES}분 넘게 게시 중</span>}
            {it.requests && it.requests.total > 0 && (
              <span className="text-micro text-muted-foreground">
                요청 {it.requests.total} · 대기 {it.requests.pending}
                {it.requests.failed > 0 && ` · 실패 ${it.requests.failed}`}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => open(refOf(it))}
            className={cn(
              'block w-full truncate text-left text-body font-medium hover:underline',
              past && 'text-muted-foreground',
            )}
          >
            {headline(it)}
          </button>
          {it.status === 'failed' && it.error_msg && <CodeBlock className="text-status-failed">{it.error_msg}</CodeBlock>}
          {moving && (
            <SlotForm
              initial={it.scheduled_at}
              label="새 예약 시각"
              submitLabel="저장"
              onCancel={() => setMoving(false)}
              onSubmit={async (iso) => {
                if (await commands.schedule(refOf(it), iso)) setMoving(false)
              }}
            />
          )}
          {!desktop && actions}
        </div>
        <Thumb it={it} />
        {desktop && actions}
      </div>
    </div>
  )
}

/** Published before today. */
function isPastPublished(it: ScheduleItem, now: Date): boolean {
  const t = parseTime(itemTime(it))
  if (!t || it.status !== 'published') return false
  return t.getTime() < new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
}

/** "지난 글 N": the earlier days of the current week, closed until asked for. */
function PastLane({ items, now, commands }: { items: ScheduleItem[]; now: Date; commands: Commands }) {
  const [shown, setShown] = useState(false)
  return (
    <section aria-label="지난 글" className="space-y-1.5">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 text-table font-semibold transition-colors hover:bg-muted"
      >
        <ChevronRight size={14} aria-hidden className={cn('text-muted-foreground transition-transform', shown && 'rotate-90')} />
        지난 글
        <span className="tabular-nums text-muted-foreground">{items.length}</span>
        <span className="text-meta font-normal text-muted-foreground">{shown ? '접기' : '펼치기'}</span>
      </button>
      {shown && (
        <TimelineList
          label="지난 글"
          items={items}
          now={now}
          getTime={itemTime}
          getKey={(it) => `${it.kind}-${it.id}`}
          getMark={markOf}
          renderItem={(it) => <ItemRow it={it} commands={commands} now={now} showTime={false} />}
        />
      )}
    </section>
  )
}

/** "슬롯 없음 N": posts waiting for a time, closed until asked for — with
 *  campaigns pausing and resuming, the lane grows long enough to push the week
 *  below the fold. */
function WaitingLane({
  items,
  initiallyShown,
  commands,
}: {
  items: ScheduleItem[]
  initiallyShown: boolean
  commands: Commands
}) {
  const [shown, setShown] = useState(initiallyShown)
  return (
    <section aria-label="슬롯 없음" className="space-y-1.5">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 text-table font-semibold transition-colors hover:bg-muted"
      >
        <ChevronRight size={14} aria-hidden className={cn('text-muted-foreground transition-transform', shown && 'rotate-90')} />
        슬롯 없음
        <span className="tabular-nums text-muted-foreground">{items.length}</span>
        <span className="text-meta font-normal text-muted-foreground">{shown ? '시각을 정해 예약한다 · 접기' : '펼치기'}</span>
      </button>
      {shown && (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {items.map((it) => (
            <li key={`${it.kind}-${it.id}`}>
              <WaitingRow it={it} commands={commands} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** A post with no slot: pick one here, or — for a campaign draft — let the
 *  campaign's pacing pick the next free one. */
function WaitingRow({ it, commands }: { it: ScheduleItem; commands: Commands }) {
  const open = useOpenDetail()
  const actions = usePostActions()
  const toast = useToast()
  return (
    <div className="flex flex-col gap-2 px-3 py-2.5 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <RowMeta it={it} />
          <StatusPill domain="post" status={it.status} scheduledAt={it.scheduled_at} />
        </div>
        <button
          type="button"
          onClick={() => open(refOf(it))}
          className="block w-full truncate text-left text-body font-medium hover:underline"
        >
          {headline(it)}
        </button>
      </div>
      <SlotForm
        label={`${headline(it, 30)} 예약 시각`}
        submitLabel="예약"
        onSubmit={(iso) => commands.schedule(refOf(it), iso)}
        extra={
          it.kind === 'draft' ? (
            <Button
              size="sm"
              icon={Sparkles}
              loading={actions.approve.isPending}
              title="캠페인 페이스에 맞춰 다음 빈 슬롯을 받는다"
              onClick={async () => {
                try {
                  const res = await actions.approve.mutateAsync({ id: it.id })
                  toast.success(res.scheduled_at ? `${formatShort(res.scheduled_at)}에 잡았다` : '슬롯을 받았다')
                } catch (e) {
                  toast.error(errorText(e))
                }
              }}
            >
              자동 배정
            </Button>
          ) : undefined
        }
      />
    </div>
  )
}

/** Desk posts for channels posted by hand. They never take a slot; their
 *  detail has the panel to put them up from. */
function ManualLane({ posts }: { posts: { id: number; platform: string; channel: string; title: string; app_id: number }[] }) {
  const open = useOpenDetail()
  const apps = useRedeemApps().data?.items ?? []
  const [shown, setShown] = useState(false)
  return (
    <section aria-label="직접 올릴 글" className="space-y-1.5">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 text-table font-semibold transition-colors hover:bg-muted"
      >
        <ChevronRight size={14} aria-hidden className={cn('text-muted-foreground transition-transform', shown && 'rotate-90')} />
        직접 올릴 글
        <span className="tabular-nums text-muted-foreground">{posts.length}</span>
        <span className="text-meta font-normal text-muted-foreground">{shown ? '접기' : '펼치기'}</span>
      </button>
      {shown && (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {posts.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2">
              <SourceTag source="redeem" />
              <PlatformMark platform={p.platform} />
              <span className="shrink-0 text-meta text-muted-foreground">
                {apps.find((a) => a.app.id === p.app_id)?.app.name ?? `app ${p.app_id}`}
                {p.channel && ` · ${p.channel}`}
              </span>
              <button
                type="button"
                onClick={() => open({ kind: 'redeem', id: p.id })}
                className="min-w-0 flex-1 truncate text-left text-body font-medium hover:underline"
              >
                {p.title}
              </button>
              <StatusPill domain="post" status="draft" />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
